import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { useModals } from '../../contexts/ModalsContext';
import { isEmpty } from 'lodash';
import dayjs from 'dayjs';
import { useSortBy, useTable } from 'react-table';
import { Button, Col, Row } from 'react-bootstrap';
import classNames from 'classnames';
import { ModalActionsEnum, CreateDocumentModalsEnum } from './DocumentTableConstants';
import { useDealId } from '../../deal-id-context';
import CreateDocumentContainer from './CreateDocumentContainer';
import { Table } from '../table';
import { getDocumentStatusIndicator } from './helpers/documentStatusIndication';
import { useDocumentWizardErrorNotifier } from '../../pages/document-wizard/shared/hooks/use-document-wizard-error-notifier/use-document-wizard-error-notifier.hook';
import { DocumentEditorErrors } from '../document-wizard/document-editor/constants/EditorConstats';
import { DocumentStatusEnum } from '../../shared/constants/document';
import {
  getDocumentAccessLabel,
  handleDocumentNavigation,
  shouldBlockOpenWithoutPossession,
} from './helpers/documentDefinitionHelpers';
import { ApprovalRequestStatus } from '../document-wizard/document-wizard-header/components/headerActions/requestReview/RequestInternalApprovalModalConstants';
import { useUserDealRestrictions } from '../../auth/user-restrictions';
import SubscriptionRequiredModal from '../../pages/modals/subscriptionModal/SubscriptionRequiredModal';
import { fuzzyInputSearch } from '../../pages/deals/helpers/determineDealsActionModalMessage';
import { Document } from '../../gql/graphql';
import { DocumentTableColumns } from './DocumentTableColumns';
import { DocumentTableProps } from './helpers/documentTableTypes';
import DocumentTableSearchbar from './DocumentTableSearchbar';
import { TitleFormattedMessage } from '../titleFormattedMessage';
import { useSlideout } from '../slideout';
import styles from './DocumentTable.module.scss';
import FilterSidebar from './filterSidebar';
import { MobileDocItem } from '../mobile-layout/MobileDocItem';
import ActivitySidebar from './activitySidebar';
import ManageParticipantsLayoutContainer from '../manage-participants/ManageParticipantsLayout';
import { useQuery } from '@apollo/client';
import { queriesV2 } from '../../api';
import PropdocsModal from '../modal/PropdocsModal';
import InviteReceivingTeamFormik from '../../forms/InviteReceivingTeamFormContainer';
import { useTeamInfo } from '../../team-id-context';
import { FormattedMessage } from 'react-intl';
import { ReactComponent as HistoryIcon } from '../../shared/icons/history-icon.svg';
import { ReactComponent as ChecklistIcon } from '../../shared/icons/checklist-icon.svg';

const DocumentTable = ({
  permissionsData,
  isDealShared,
  teamId,
  dealDocumentsData,
  alertableDocuments,
  onChecklistClick,
}: DocumentTableProps) => {
  const manageParticipantsSlideout = useSlideout();
  const { formatMessage } = useIntl();
  const { canCreateBlankDocument, canCreateDocumentFromTemplate, canCreateDocumentFromWord, canUploadDocument } =
    useUserDealRestrictions();
  const dealId = useDealId();
  const navigate = useNavigate();
  const notifyError = useDocumentWizardErrorNotifier();

  // Get deal data for manage participants slideout
  // Only fetch when slideout is actually open to avoid duplicate queries
  const {
    loading: isDealDataLoading,
    data: dealData,
    refetch,
  } = useQuery(queriesV2.getDealTeamsByTeamId, {
    skip: !manageParticipantsSlideout.isOpen || !teamId || !dealId,
    variables: {
      dealId,
      teamId,
    },
    fetchPolicy: 'cache-and-network',
  });

  const refetchDealData = async () => {
    await refetch();
  };
  const [showSubscriptionRequiredModal, setSubscriptionRequiredModal] = useState(false);
  const [showInviteCounterpartyModal, setShowInviteCounterpartyModal] = useState(false);
  const { teamId: currentTeamId } = useTeamInfo();
  const [filterTitleText, setFilterTitleText] = useState('');
  const [filterStatuses, setFilterStatuses] = useState<Set<string>>(new Set());
  const isDocumentsDataAvailible = !isEmpty(dealDocumentsData);
  const [showFilter, setShowFilter] = useState(false);
  const [showActivity, setShowActivity] = useState(false);
  const [sorting, setSorting] = useState('');

  const hasUserEditPermission = !!permissionsData?.hasEditPermission;

  const alertableDocumentsIDs = useMemo(() => {
    return alertableDocuments?.map((document: Document) => document.id);
  }, [alertableDocuments]);

  const { setActiveModal } = useModals();

  const handleTemplateSelectorModal = useCallback(() => {
    return canCreateDocumentFromTemplate
      ? setActiveModal(CreateDocumentModalsEnum.TEMPLATE_SELECTOR)
      : setSubscriptionRequiredModal(true);
  }, [canCreateDocumentFromTemplate, setActiveModal]);

  const handleCreateBlankDocumentModal = useCallback(() => {
    return canCreateBlankDocument
      ? setActiveModal(CreateDocumentModalsEnum.BLANK_DOCUMENT)
      : setSubscriptionRequiredModal(true);
  }, [canCreateBlankDocument, setActiveModal]);

  const handleFileUploadModal = useCallback(() => {
    return canUploadDocument
      ? setActiveModal(ModalActionsEnum.UPLOAD_FILE)
      : setSubscriptionRequiredModal(true);
  }, [canUploadDocument, setActiveModal]);

  const handleDocFileUploadModal = useCallback(() => {
    return canCreateDocumentFromWord
      ? setActiveModal(ModalActionsEnum.UPLOAD_DOC_FILE)
      : setSubscriptionRequiredModal(true);
  }, [canCreateDocumentFromWord, setActiveModal]);

  // The engine decides whether a document is openable without possession — see
  // shouldBlockOpenWithoutPossession.
  const editorEngineFor = useCallback(
    (documentId: string): string | null => {
      const row = (dealDocumentsData ?? []).find((d: Document) => String(d.id) === String(documentId));
      return ((row?.editorConfiguration as Record<string, any> | null)?.editor_engine as string) ?? null;
    },
    [dealDocumentsData]
  );

  const handleEditDocument = useCallback(
    (documentId: string, hasCurrentTeamPossession: boolean, documentStatus: string) => {
      const isDocumentStatusPreparingToSign = documentStatus === DocumentStatusEnum.PREPARING_TO_SIGN;

      if (
        shouldBlockOpenWithoutPossession({
          documentStatus,
          hasCurrentTeamPossession,
          editorEngine: editorEngineFor(documentId),
        })
      ) {
        return notifyError({ message: DocumentEditorErrors.NO_POSSESSION.defaultMessage, pinned: true });
      }

      if (!hasCurrentTeamPossession && isDocumentStatusPreparingToSign) {
        return notifyError({ message: DocumentEditorErrors.IS_PREPARING_TO_SIGN.defaultMessage });
      }

      if (documentId) {
        handleDocumentNavigation({ documentId, dealId, teamId, documentStatus, navigate });
      }
    },
    [dealId, teamId, notifyError, navigate, editorEngineFor]
  );

  const columns = useMemo(
    () =>
      DocumentTableColumns({
        alertableDocumentsIDs,
        teamId,
        dealId,
        handleEditDocument,
        isDealShared,
        permissions: permissionsData,
      }),
    [alertableDocumentsIDs, teamId, dealId, handleEditDocument, isDealShared, permissionsData]
  );

  const allStatuses = useMemo(() => {
    if (!dealDocumentsData) {
      return [];
    }
    return dealDocumentsData.map((document: Document) =>
      document.approvalRequest?.status === ApprovalRequestStatus.PENDING && document.currentTeamPossession
        ? 'Approval Pending'
        : getDocumentStatusIndicator(document.status as DocumentStatusEnum, !!document.currentTeamPossession)
    );
  }, [dealDocumentsData]);

  useEffect(() => {
    if (allStatuses.length === 0) return;

    if (filterStatuses.size === 0) {
      // Initial load - select all statuses
      setFilterStatuses(new Set(allStatuses));
    } else {
      // After mutations - add new statuses to filter while preserving existing selections
      const uniqueStatuses = Array.from(new Set(allStatuses));
      const newStatuses = uniqueStatuses.filter(status => !filterStatuses.has(status));

      if (newStatuses.length > 0) {
        const updatedFilterStatuses = new Set(filterStatuses);
        newStatuses.forEach(status => updatedFilterStatuses.add(status));
        setFilterStatuses(updatedFilterStatuses);
      }
    }
  }, [allStatuses]); // Only depend on allStatuses to allow updates when new statuses appear

  const handleManageDealParticipantsClick = () => {
    manageParticipantsSlideout.open();
  };

  const documentsData = useMemo(() => {
    if (!dealDocumentsData) return [];

    const docData = dealDocumentsData
      .filter((document: Document) => {
        if (
          (filterStatuses.size &&
            !filterStatuses.has(
              document.approvalRequest?.status === ApprovalRequestStatus.PENDING && document.currentTeamPossession
                ? 'Approval Pending'
                : getDocumentStatusIndicator(document.status as DocumentStatusEnum, !!document.currentTeamPossession)
            )) ||
          !filterStatuses.size
        ) {
          return false;
        }

        if (filterTitleText && !fuzzyInputSearch({ filteredText: filterTitleText, searchValue: document.title })) {
          return false;
        }
        return true;
      })
      .map((document: Document) => {
        return {
          id: document.id,
          name: formatMessage({
            id: `format.name${document.id}`,
            defaultMessage: document.title,
          }),
          status:
            document.approvalRequest?.status === ApprovalRequestStatus.PENDING && document.currentTeamPossession
              ? 'Approval Pending'
              : document.status,
          document,
          access: getDocumentAccessLabel(document.receiverTeam),
          onRowClick: () => {
            handleEditDocument(document.id, !!document.currentTeamPossession, document.status || '');
          },
        };
      });

    docData
      .sort((a: any, b: any) => {
        switch (sorting) {
          case 'ascName':
            return a.name.localeCompare(b.name);

          case 'descName':
            return b.name.localeCompare(a.name);

          case 'accessMyTeam':
            return a.access?.localeCompare(b.access || '');

          case 'accessPrivate':
            return b.access?.localeCompare(a.access || '');

          default:
            return dayjs(b.document.lastUpdated).isBefore(a.document.lastUpdated) ? -1 : 1;
        }
      })
      .sort((a: { document: Document }) => {
        return alertableDocumentsIDs?.includes(a.document.id) ? -1 : 1;
      });

    return docData;
  }, [
    dealDocumentsData,
    formatMessage,
    handleEditDocument,
    sorting,
    filterTitleText,
    filterStatuses,
    alertableDocumentsIDs,
  ]);

  const table = useTable(
    {
      data: documentsData,
      //@ts-ignore
      columns,
    },
    useSortBy
  );

  return (
    <>
      <Row className={'mb-3 align-items-center'}>
        <Col className={classNames(styles.documentButtons, 'd-flex justify-content-between')}>
          <DocumentTableSearchbar
            filterTitleText={filterTitleText}
            setFilterTitleText={setFilterTitleText}
            filterStatuses={filterStatuses}
            allStatuses={allStatuses}
            setShowFilter={setShowFilter}
            setFilterStatuses={setFilterStatuses}
          />

          <div className={classNames(styles.documentButtons__btns, 'd-flex')}>
            <Button variant="primary" onClick={handleManageDealParticipantsClick}>
              <span className="d-none d-md-inline">
                <TitleFormattedMessage id="cta.add-new-template" defaultMessage="Manage People" />
              </span>
              <span className="d-md-none">
                <TitleFormattedMessage id="cta.add-new-template-mobile" defaultMessage="People" />
              </span>
            </Button>

            {hasUserEditPermission && (
              <div className={'ml-3'}>
                <CreateDocumentContainer
                  handleTemplateSelectorModal={handleTemplateSelectorModal}
                  handleFileUploadModal={handleFileUploadModal}
                  handleDocFileUploadModal={handleDocFileUploadModal}
                  handleCreateBlankDocumentModal={handleCreateBlankDocumentModal}
                  hasReceiverEditPermission={hasUserEditPermission}
                  hasDealDocuments
                />
              </div>
            )}

            <Button className="filterBtn ml-3 d-md-none" onClick={() => setShowActivity(true)}>
              <HistoryIcon width={24} height={24} />
            </Button>

            <Button
              className="filterBtn ml-3 d-md-none"
              onClick={() => {
                onChecklistClick?.();
              }}
            >
              <ChecklistIcon width={24} height={24} />
            </Button>
          </div>
        </Col>
      </Row>
      <Row className={styles.documentTableCard}>
        {isDocumentsDataAvailible ? (
          <Col className={styles.documentDataTable}>
            <div className="non-mobile">
              <Table striped isStickyHeader table={table} isActionColumn />
            </div>
            <div className="mobile">
              {documentsData.map((document) => (
                <MobileDocItem
                  key={document.id}
                  doc={document}
                  teamId={teamId}
                  dealId={dealId}
                  isDealShared={isDealShared}
                  permissions={permissionsData}
                  handleEdit={handleEditDocument}
                />
              ))}
            </div>
          </Col>
        ) : (
          <Col className={styles.documentDataTable}>
            <div className="text-center py-5">
              <p className="text-muted">
                <FormattedMessage id="documents.none-created" defaultMessage="No documents created for this Deal" />
              </p>
            </div>
          </Col>
        )}
      </Row>
      <SubscriptionRequiredModal
        isOpen={showSubscriptionRequiredModal}
        onClose={() => setSubscriptionRequiredModal(false)}
      />
      <FilterSidebar
        isOpen={showFilter}
        filterStatuses={filterStatuses}
        allStatuses={allStatuses}
        onClose={() => setShowFilter(!showFilter)}
        setSorting={setSorting}
        setFilterStatuses={setFilterStatuses}
      />
      <ActivitySidebar isOpen={showActivity} onClose={() => setShowActivity(!showActivity)} />

      {/* Manage Participants Slideout */}
      <ManageParticipantsLayoutContainer
        isDealDataLoading={isDealDataLoading}
        dealData={dealData}
        refetchDealData={refetchDealData}
        isOpen={manageParticipantsSlideout.isOpen}
        onClose={manageParticipantsSlideout.close}
        onShowInviteCounterpartyModal={() => setShowInviteCounterpartyModal(true)}
        onShowSubscriptionRequiredModal={() => setSubscriptionRequiredModal(true)}
      />

      {/* Invite Counterparty Modal - rendered outside slideout */}
      <PropdocsModal
        show={showInviteCounterpartyModal}
        setShow={() => setShowInviteCounterpartyModal(false)}
        size="lg"
        title={
          <header>
            <FormattedMessage id="title.add-team-member" defaultMessage="Invite Counterparty to the Deal" />
          </header>
        }
      >
        <InviteReceivingTeamFormik
          bodyContainer={React.Fragment}
          actionsContainer={React.memo(({ children }: { children?: React.ReactNode }) => (
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', marginTop: '1.5rem' }}>
              {children}
            </div>
          ))}
          renderSecondaryAction={() => (
            <Button variant="link" className="text-secondary" onClick={() => setShowInviteCounterpartyModal(false)}>
              <TitleFormattedMessage id="cta.cancel" defaultMessage="Cancel" />
            </Button>
          )}
          sendDocumentToReceiverHandler={() => {}}
          onSuccess={async () => {
            await refetchDealData();
            setShowInviteCounterpartyModal(false);
          }}
          teamId={currentTeamId}
          sendWithDocumentId={false}
          shareDocument={false}
          refreshCounterpartyData={refetchDealData}
        />
      </PropdocsModal>
    </>
  );
};

export default DocumentTable;
